import { useState } from 'react';
import { FiImage, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import { request } from '../helpers/request';
import uploadImage from '../helpers/uploadImage';
import StarRating from './StarRating';

const ReviewForm = ({ productId, onReviewSubmitted, existingReview = null, onCancel }) => {
    const [rating, setRating] = useState(existingReview?.rating || 0);
    const [title, setTitle] = useState(existingReview?.title || '');
    const [comment, setComment] = useState(existingReview?.comment || '');
    const [images, setImages] = useState(existingReview?.images || []);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (images.length + files.length > 3) {
            toast.error('Maximum 3 images allowed');
            return;
        }

        setUploading(true);

        try {
            const uploadPromises = files.map(async (file) => {
                const data = await uploadImage(file);
                return data?.secure_url;
            });

            const uploadedUrls = await Promise.all(uploadPromises);
            const filteredUrls = uploadedUrls.filter(Boolean);
            
            if (filteredUrls.length > 0) {
                setImages([...images, ...filteredUrls]);
                toast.success('Images uploaded successfully');
            } else {
                toast.error('Failed to upload images');
            }
        } catch (error) {
            toast.error('Failed to upload images');
        } finally {
            setUploading(false);
        }
    };

    const removeImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }

        if (!title.trim()) {
            toast.error('Please enter a review title');
            return;
        }

        if (!comment.trim()) {
            toast.error('Please write a review');
            return;
        }

        setSubmitting(true);

        try {
            const endpoint = existingReview 
                ? SummaryApi.updateReview 
                : SummaryApi.createReview;

            const payload = {
                productId,
                rating,
                title: title.trim(),
                comment: comment.trim(),
                images
            };

            if (existingReview) {
                payload.reviewId = existingReview._id;
            }

            const response = await request({
                url: endpoint.url,
                method: endpoint.method,
                data: payload
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || 'Review submitted successfully');
                onReviewSubmitted();
            } else {
                toast.error(data.message || 'Failed to submit review');
            }
        } catch (error) {
            toast.error('Failed to submit review. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold mb-4">
                {existingReview ? 'Update Your Review' : 'Write a Review'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Rating */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rating <span className="text-red-500">*</span>
                    </label>
                    <StarRating 
                        rating={rating} 
                        size="lg" 
                        interactive={true}
                        onRatingChange={setRating}
                    />
                </div>

                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Review Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Summarize your experience"
                        maxLength={100}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{title.length}/100</p>
                </div>

                {/* Comment */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Review <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Share your detailed experience with this product"
                        maxLength={1000}
                        rows={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">{comment.length}/1000</p>
                </div>

                {/* Images */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Add Photos (Optional)
                    </label>
                    
                    {images.length < 3 && (
                        <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md cursor-pointer hover:bg-gray-200 transition-colors">
                            <FiImage />
                            <span>{uploading ? 'Uploading...' : 'Upload Images'}</span>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                disabled={uploading}
                                className="hidden"
                            />
                        </label>
                    )}

                    {images.length > 0 && (
                        <div className="flex gap-2 mt-3">
                            {images.map((img, index) => (
                                <div key={index} className="relative w-20 h-20 rounded-md overflow-hidden border border-gray-300">
                                    <img src={img} alt={`Review ${index + 1}`} className="w-full h-full object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    >
                                        <FiX size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                    <button
                        type="submit"
                        disabled={submitting || rating === 0}
                        className="flex-1 px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                        {submitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
                    </button>
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default ReviewForm;
